"use client";

import { usePathname } from "next/navigation";
import { megaMenuData } from "@/lib/navigation";
import { getPageColorsFromRoute } from "@/lib/navigation";
import Link from "next/link";
import { 
  Cog, 
  Target, 
  Building2,
  ShoppingBag,
  Users,
  FileText,
  BarChart3,
  Settings
} from "lucide-react";

// Icon mapping for different menu sections
const menuIcons: Record<string, React.ReactNode> = {
  "Services": <Cog className="w-5 h-5 text-blue-600" />,
  "Solutions": <Target className="w-5 h-5 text-purple-600" />,
  "Company": <Building2 className="w-5 h-5 text-green-600" />,
  "Products": <ShoppingBag className="w-5 h-5 text-orange-600" />,
  "Resources": <FileText className="w-5 h-5 text-indigo-600" />,
  "Analytics": <BarChart3 className="w-5 h-5 text-pink-600" />,
  "Support": <Users className="w-5 h-5 text-teal-600" />,
};

interface MiniNavBarProps {
  /** "light" renders white/light text for dark backgrounds */
  variant?: "default" | "light";
}

export function MiniNavBar({ variant = "default" }: MiniNavBarProps) {
  const pathname = usePathname();
  const pageColors = getPageColorsFromRoute(pathname || "");
  const isLight = variant === "light";

  // Find the current megamenu section based on pathname
  const getCurrentSection = () => {
    for (const menu of megaMenuData) {
      for (const section of menu.sections) {
        for (const item of section.items) {
          if (pathname?.startsWith(item.href)) {
            return { menu, section };
          }
        }
      }
    }
    return null;
  };

  const currentSection = getCurrentSection();

  if (!currentSection) return null;

  const { menu, section } = currentSection;
  
  // Show max 5 links
  const linksToShow = section.items.slice(0, 5);
  const menuIconLight: Record<string, React.ReactNode> = {
    "Services": <Cog className="w-5 h-5 text-white/60" />,
    "Solutions": <Target className="w-5 h-5 text-white/60" />,
    "Company": <Building2 className="w-5 h-5 text-white/60" />,
    "Products": <ShoppingBag className="w-5 h-5 text-white/60" />,
    "Resources": <FileText className="w-5 h-5 text-white/60" />,
    "Analytics": <BarChart3 className="w-5 h-5 text-white/60" />,
    "Support": <Users className="w-5 h-5 text-white/60" />,
  };
  const resolvedIcon = isLight
    ? (menuIconLight[menu.title] || <Settings className="w-5 h-5 text-white/60" />)
    : (menuIcons[menu.title] || <Settings className="w-5 h-5 text-gray-600" />);

  return (
    <div className="w-full bg-transparent">
      {/* Single row layout - works across all breakpoints */}
      {/* No internal padding - parent component (PageHero/Section) handles responsive padding */}
      <div className="flex items-center gap-4 sm:gap-6 pb-2">
        {/* Left side - Icon + Menu title (raised above fades) */}
        <div className="flex items-center gap-3 flex-shrink-0 z-20 pr-2">
          <div className="flex-shrink-0 mr-1 z-20">
            {resolvedIcon}
          </div>
          <h3 className={`text-base sm:text-lg xl:text-xl font-black leading-none whitespace-nowrap z-20 ${isLight ? "text-white" : "text-black"}`}>
            {menu.title}
          </h3>
        </div>
        
        {/* Right side - Scrollable nav links with fade edges */}
        <div className="relative flex-1 min-w-0 flex justify-end pr-2 sm:pr-3 md:pr-5 xl:pr-6">       
          {/* Scrollable container - no internal padding since parent provides it */}
          <nav 
            className="flex items-baseline pl-1 sm:pl-2 md:pl-2.5 xl:pl-3 gap-x-2.5 sm:gap-x-3.5 md:gap-x-4.5 xl:gap-x-5 overflow-x-auto max-w-full"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <style jsx>{`nav::-webkit-scrollbar { display: none; }`}</style>
            {linksToShow.map((item, index) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={index}
                  href={item.href}
                  className={`text-sm sm:text-md md:text-md xl:text-base font-medium sm:font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 py-1 ${
                    isActive
                      ? "text-[color:var(--mininav-active-color)]"
                      : isLight
                        ? "text-white hover:text-[color:var(--mininav-active-color)]"
                        : "text-gray-500 hover:text-[color:var(--mininav-active-color)]"
                  }`}
                >
                  {item.title}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
}
